import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchActiveRateCard, updateRateCard, fetchProposalTemplate, fetchProposalPackages,
  fetchAllPlans, patchProposalTemplate, deleteCustomSection,
} from "../services/rateCardService";

/**
 * The active rate card. Both the master screen and the proposal form read it —
 * the proposal form only to show recommended rates and limits; the prices
 * themselves always come from /proposals/calculate.
 */
export const useActiveRateCard = () =>
  useQuery({
    queryKey: ["rate-card", "active"],
    queryFn: fetchActiveRateCard,
    staleTime: 0,
    refetchOnMount: "always",
  });

/* No stale window on any of these: an edit to the rate card or the template has
   to show up the next time a screen opens it, or a rep quotes yesterday's
   prices. They are small calls; correctness beats saving one request. */
/**
 * @param {string} [segment] pass it on the document side to get the `resolved`
 *        block for that segment; leave it out in the content editor, which needs
 *        every segment's raw keys.
 */
export const useProposalTemplate = (segment) =>
  useQuery({
    queryKey: ["proposal-template", segment || "raw"],
    queryFn: () => fetchProposalTemplate(segment),
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useProposalPackages = (segment) =>
  useQuery({
    queryKey: ["proposal-packages", segment],
    queryFn: () => fetchProposalPackages(segment),
    enabled: !!segment,
    staleTime: 0,
    refetchOnMount: "always",
  });

/** Saves one section of the document content at a time. */
export const usePatchProposalTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => patchProposalTemplate(patch),
    onSuccess: () => {
      toast.success("Content saved");
      /* Every segment's copy of the template goes stale at once — the key has a
         segment in it, so this has to match on the prefix. */
      queryClient.invalidateQueries({ queryKey: ["proposal-template"], refetchType: "all" });
    },
    onError: (err) => toast.error(err?.message || "The content could not be saved"),
  });
};

/**
 * Deletes one custom section and hands back the new arrays.
 *
 * The response is written straight into the template cache, so the screen shows
 * the result without a refetch — and without the section reappearing from a
 * stale copy.
 */
export const useDeleteCustomSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteCustomSection(id),
    onSuccess: (data) => {
      toast.success("Section deleted");
      queryClient.setQueriesData({ queryKey: ["proposal-template"] }, (old) =>
        old ? { ...old, ...data } : old);
    },
    onError: (err) => toast.error(err?.message || "The section could not be deleted"),
  });
};

/** Only needed while editing — the plan dropdown behind each tier. */
export const useAllPlans = (enabled = true) =>
  useQuery({
    queryKey: ["all-plans"],
    queryFn: fetchAllPlans,
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useUpdateRateCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, config }) => updateRateCard(id, { name, config }),
    onSuccess: () => {
      toast.success("Rate card saved");
      queryClient.invalidateQueries({ queryKey: ["rate-card"], refetchType: "all" });
    },
    // The service layer has already turned 403 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "The rate card could not be saved"),
  });
};
